/**
 * Livechat settings: language and voice gender.
 * Used by the settings dialog and passed to AltairAgent for connect config.
 */

import React, { createContext, FC, ReactNode, useContext } from 'react';

export type LivechatLanguage = 'ar' | 'en' | 'ur';
export type LivechatVoiceGender = 'male' | 'female';

export interface LivechatSettingsContextValue {
  language: LivechatLanguage;
  setLanguage: (lang: LivechatLanguage) => void;
  voiceGender: LivechatVoiceGender;
  setVoiceGender: (gender: LivechatVoiceGender) => void;
}

const LivechatSettingsContext = createContext<LivechatSettingsContextValue | undefined>(undefined);

export interface LivechatSettingsProviderProps {
  children: ReactNode;
  language: LivechatLanguage;
  setLanguage: (lang: LivechatLanguage) => void;
  voiceGender: LivechatVoiceGender;
  setVoiceGender: (gender: LivechatVoiceGender) => void;
}

export const LivechatSettingsProvider: FC<LivechatSettingsProviderProps> = ({
  children,
  language,
  setLanguage,
  voiceGender,
  setVoiceGender,
}) => {
  const value: LivechatSettingsContextValue = {
    language,
    setLanguage,
    voiceGender,
    setVoiceGender,
  };
  return (
    <LivechatSettingsContext.Provider value={value}>
      {children}
    </LivechatSettingsContext.Provider>
  );
};

export function useLivechatSettings(): LivechatSettingsContextValue {
  const context = useContext(LivechatSettingsContext);
  if (context === undefined) {
    throw new Error('useLivechatSettings must be used within a LivechatSettingsProvider');
  }
  return context;
}
